import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useStore } from '@nanostores/react';
import classNames from 'classnames';
import { noop } from 'lodash-es';

import type { AssetsSegmentSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import {
  Alert,
  AlertDescription,
  Button,
  type ButtonProps,
  FileTrigger,
  Icon,
  type SuggestionsPluginItem,
  Tooltip,
  TooltipTrigger,
} from '@iconik-io/uad';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { serializeUnifiedAnnotation } from '../../../../utils/annotation/serialize_unified_annotation';
import {
  type CommentAttachmentTileItem,
  CommentAttachmentTiles,
} from '../comment_attachments/comment_attachment_tiles/comment_attachment_tiles';
import { ACCEPTED_ATTACHMENT_TYPES } from '../comment_attachments/utils/attachment_files';
import { CommentInput } from '../comment_input/comment_input';
import { segmentColors } from '../segment/constants';
import { SegmentColorMarker } from '../segment/segment_color_marker';
import { ColorSelectMenu } from '../ui/color_select_menu/color_select_menu';
import { CommentTypeMenu } from '../ui/comment_type_select_menu/comment_type_select_menu';
import { Form } from '../ui/form/form';
import { SEGMENT_TYPE_COMMENT } from './constants';
import type { ToolbarMode } from './types';

import styles from './comment_form.module.css';

export type CommentFormAttachments = {
  items: CommentAttachmentTileItem[];
  hasAttachments: boolean;
  isUploading: boolean;
  onAdd: (files: File[]) => void;
  onRemove: (item: CommentAttachmentTileItem) => void;
};

type CommentFormProps = {
  className?: string;
  inputClassName?: string;
  bottomButtons?: ReactElement;
  disabled?: boolean;
  onChange?: (segment: Partial<AssetsSegmentSchemaPatched>) => void;
  onLayoutChanged?: () => void;
  onSubmit?: (segment: AssetsSegmentSchemaPatched) => Promise<unknown>;
  canComment?: boolean;
  onIdentificationRequired?: () => void;
  onCancel?: () => void;
  segment: AssetsSegmentSchemaPatched;
  withColorsPicker?: boolean;
  autoFocus?: boolean;
  submitButton?: (props: { isDisabled: boolean }) => ReactNode;
  isCompact?: boolean;
  inputPlaceholder?: string;
  onFocus?: () => void;
  canMention: boolean;
  filterMentionResults?: (
    query: string | null,
  ) => Promise<SuggestionsPluginItem[]>;
  // External trigger to force-reset the editor without remounting the whole form
  resetCounter?: number;
  isInternalUser?: boolean;
  commentVersion?: string;
  // Persisted visibility of the segment being edited, used to detect a change
  // and warn that replies in the thread will also be updated.
  originalIsInternal?: boolean | null;
  replyCount?: number;
  attachments?: CommentFormAttachments;
};

type DefaultSubmitButtonProps = Pick<
  ButtonProps,
  'isDisabled' | 'isPending' | 'preventFocusOnPress'
>;

export function DefaultSubmitButton(props: DefaultSubmitButtonProps) {
  return (
    <Button size="s" variant="primary" type="submit" {...props}>
      Save
    </Button>
  );
}

export function CommentForm(props: CommentFormProps) {
  const {
    className,
    inputClassName,
    disabled,
    onChange,
    onLayoutChanged = noop,
    onSubmit,
    canComment = true,
    onIdentificationRequired,
    onCancel,
    segment,
    withColorsPicker,
    autoFocus,
    submitButton,
    isCompact,
    inputPlaceholder,
    onFocus,
    canMention,
    filterMentionResults,
    resetCounter,
    isInternalUser,
    commentVersion,
    originalIsInternal,
    replyCount,
    attachments,
  } = props;

  const { store: unifiedPlayerStore } = useBasePlayerContext();
  const [toolbarMode, setToolbarMode] = useState<ToolbarMode>();
  const personalDraftAnnotation = useStore(
    unifiedPlayerStore.segment.annotation.$personalDraftAnnotation,
  );
  const drawing = useMemo(
    () => serializeUnifiedAnnotation(personalDraftAnnotation),
    [personalDraftAnnotation],
  );
  const hasContent =
    Boolean(segment.segment_text?.trim()) ||
    Boolean(drawing) ||
    Boolean(attachments?.hasAttachments);
  const isSubmitDisabled = !hasContent || Boolean(attachments?.isUploading);

  const formRef = useRef<HTMLFormElement>(null);

  const [isPending, setIsPending] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  // When external reset counter changes, trigger a local editor reset
  useEffect(() => {
    if (resetCounter !== undefined) {
      setResetKey((prev) => prev + 1);
    }
  }, [resetCounter]);

  const handleColorChange = useCallback(
    (color: string | undefined) => {
      onChange?.({ segment_color: color });
    },
    [onChange],
  );

  const handleCommentTypeChange = useCallback(
    (value: string | undefined) => {
      onChange?.({ is_internal: value === 'is_internal' });
    },
    [onChange],
  );

  const handleSubmitRequested = useCallback(() => {
    formRef.current?.requestSubmit();
  }, []);

  const handleSubmit = useCallback(
    (formData: AssetsSegmentSchemaPatched) => {
      if (!canComment) {
        onIdentificationRequired?.();
        return;
      }
      if (!onSubmit || isPending || !segment.version_id || isSubmitDisabled) {
        return;
      }
      setIsPending(true);
      const newSegment: AssetsSegmentSchemaPatched = {
        ...formData,
        segment_color: segment.segment_color ?? undefined,
        segment_type: 'COMMENT',
        asset_id: segment.asset_id,
        is_internal: segment.is_internal,
        version_id: commentVersion,
      };
      if (drawing) {
        newSegment.drawing = drawing;
        newSegment.has_drawing = true;
      }
      const currentTime = unifiedPlayerStore.playableMedia.$currentTime.get();
      const range = unifiedPlayerStore.segment.$currentSegment.get().range;
      let start;
      let end;
      if (currentTime !== undefined) {
        start = currentTime * 1000;
        end = currentTime * 1000;
      }
      if (range) {
        start = range.start * 1000;
        end = range.end * 1000;
      }

      newSegment.time_start_milliseconds = start;
      newSegment.time_end_milliseconds = end;

      onSubmit(newSegment)
        .then(() => {
          // Remount Editor component to reset
          setResetKey((prev) => prev + 1);

          if (segment.segment_type === SEGMENT_TYPE_COMMENT) {
            // TODO onboarding https://linear.app/iconik/issue/RP-1521
            // dispatch(
            //   setGettingStartedCheck({ name: 'hasLeftComment', value: true }),
            // );
          }
        })
        .finally(() => {
          setIsPending(false);
        });
    },
    [
      drawing,
      isSubmitDisabled,
      canComment,
      onIdentificationRequired,
      onSubmit,
      isPending,
      segment.segment_type,
      segment.version_id,
      segment.asset_id,
      segment.segment_color,
      segment.is_internal,
      commentVersion,
    ],
  );

  const handleCommentColorButtonClick = useCallback(() => {
    setToolbarMode(
      toolbarMode === 'comment_color' ? undefined : 'comment_color',
    );
  }, [toolbarMode]);

  const visibilityChanged =
    Boolean(segment.id) &&
    Boolean(segment.is_internal) !== Boolean(originalIsInternal);
  const affectedReplyCount = replyCount ?? 0;
  const showVisibilityWarning = visibilityChanged && affectedReplyCount > 0;
  const affectedReplyLabel = affectedReplyCount === 1 ? 'reply' : 'replies';
  const nextVisibilityLabel = segment.is_internal ? 'internal' : 'public';

  const handleChange = (value: string) => {
    onChange?.({ segment_text: value });
  };

  return (
    <Form
      className={classNames(styles.form, className, {
        [styles.compact]: isCompact,
        [styles.internal]: segment.is_internal && !segment.id,
      })}
      ref={formRef}
      onSubmit={handleSubmit}
      aria-label="Segment form"
    >
      {!isCompact ? <SegmentColorMarker segment={segment} /> : null}

      <div className={styles.form_content}>
        <div>
          <CommentInput
            className={inputClassName}
            value={segment.segment_text ?? ''}
            disabled={disabled || !canComment}
            onLayoutChanged={onLayoutChanged}
            onSubmitRequested={handleSubmitRequested}
            color={segment.segment_color}
            onChange={handleChange}
            autoFocus={autoFocus}
            placeholder={inputPlaceholder}
            onFocus={onFocus}
            canMention={canMention}
            filterMentionResults={filterMentionResults}
            onClick={canComment ? undefined : onIdentificationRequired}
            // Aggregate internal and external reset triggers
            resetCounter={resetKey + (resetCounter ?? 0)}
          />
        </div>

        {attachments ? (
          <CommentAttachmentTiles
            items={attachments.items}
            onRemove={disabled || isPending ? undefined : attachments.onRemove}
          />
        ) : null}

        {showVisibilityWarning ? (
          <Alert variant="warning">
            <AlertDescription>
              {`${affectedReplyCount} ${affectedReplyLabel} in this thread will also be made ${nextVisibilityLabel} when you save.`}
            </AlertDescription>
          </Alert>
        ) : null}

        <div className={classNames(styles.controls_row)}>
          <div className={styles.controls_modifiers}>
            {withColorsPicker ? (
              <ColorSelectMenu
                onPress={handleCommentColorButtonClick}
                colorItems={segmentColors}
                selection={segment.segment_color ?? null}
                onSelectionChange={(key) => {
                  handleColorChange(key ?? undefined);
                }}
              />
            ) : null}
            {attachments ? (
              <AttachFilesButton
                canComment={canComment}
                isDisabled={disabled || isPending}
                onIdentificationRequired={onIdentificationRequired}
                onAdd={attachments.onAdd}
              />
            ) : null}
            {isInternalUser ? (
              <CommentTypeMenu
                selectedKey={segment.is_internal ? 'is_internal' : 'public'}
                onSelectionChange={(key) => {
                  handleCommentTypeChange(key ?? undefined);
                }}
              />
            ) : null}
          </div>
          {isCompact ? (
            <div className={styles.controls_actions}>
              <Button
                size="s"
                variant="tertiary"
                onPress={onCancel}
                isDisabled={disabled}
              >
                Cancel
              </Button>
              {submitButton?.({
                isDisabled: Boolean(disabled) || isSubmitDisabled,
              }) ?? (
                <DefaultSubmitButton
                  preventFocusOnPress
                  isPending={Boolean(disabled)}
                  isDisabled={disabled || isSubmitDisabled}
                />
              )}
            </div>
          ) : (
            <TooltipTrigger>
              <Button
                size="m"
                variant="primary"
                isPending={isPending}
                pendingLabel="Saving…"
                preventFocusOnPress
                isDisabled={isSubmitDisabled}
                type="submit"
                icon={<Icon name="send" />}
                aria-label="Send comment"
              />
              <Tooltip>Submit comment (Ctrl/Cmd + Enter)</Tooltip>
            </TooltipTrigger>
          )}
        </div>
      </div>

      {/* Dummy to maintain even gaps within flex (color bar | content | dummy) */}
      {!isCompact ? <div /> : null}
    </Form>
  );
}

type AttachFilesButtonProps = {
  canComment: boolean;
  isDisabled?: boolean;
  onIdentificationRequired?: () => void;
  onAdd: (files: File[]) => void;
};

function AttachFilesButton({
  canComment,
  isDisabled,
  onIdentificationRequired,
  onAdd,
}: AttachFilesButtonProps) {
  const button = (
    <Button
      size="s"
      variant="tertiary"
      icon={<Icon name="attachment" />}
      aria-label="Attach images"
      isDisabled={isDisabled}
      onPress={canComment ? undefined : onIdentificationRequired}
    />
  );

  return (
    <TooltipTrigger>
      {canComment ? (
        <FileTrigger
          acceptedFileTypes={ACCEPTED_ATTACHMENT_TYPES}
          allowsMultiple
          onSelect={(files) => {
            if (files) {
              onAdd(Array.from(files));
            }
          }}
        >
          {button}
        </FileTrigger>
      ) : (
        button
      )}
      <Tooltip>Attach images</Tooltip>
    </TooltipTrigger>
  );
}
